import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { directUploadWithProgress } from "@core/features/uploadFile";
import { useCompleteOrgAction, ACTION_TYPE_SLUG } from "@core/features/actions";
import { FileType } from "@core/types/file.types";
import { LogosUpload } from "./LogosUpload";

export type Props = {
  /** Optional organization ID override (defaults to URL param) */
  organizationId?: number;
  /** Callback when all files have been uploaded */
  onUploadComplete?: () => void;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Callback when upload state changes */
  onUploadStateChange?: (state: { hasFiles: boolean; isAltTextValid: boolean }) => void;
  /** Initial files to display (previously uploaded) */
  initialFiles?: FileType[];
  /** Show remove button for completed files */
  showRemoveButton?: boolean;
  /** Callback when a file is removed */
  onFileRemove?: (fileId: number) => void;
};

/**
 * Container component for LogosUpload.
 * Handles file upload with organization context and cache invalidation.
 *
 * Note: Authorization is handled at the page level (OrganizationAdminPage).
 * This container trusts that it's only rendered for authorized users.
 */
export function LogosUploadContainer({
  organizationId: organizationIdProp,
  onUploadComplete,
  maxFiles,
  onUploadStateChange,
  initialFiles,
  showRemoveButton,
  onFileRemove,
}: Props) {
  const params = useParams<{ organizationId: string }>();
  const organizationId = organizationIdProp ?? Number(params.organizationId);
  const queryClient = useQueryClient();

  const { complete: completeOrgUploadLogo } = useCompleteOrgAction({
    actionTypeSlug: ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO,
    organizationId,
  });

  const uploadFileHandler = (
    file: File,
    onProgress: (progress: number) => void
  ) => {
    return directUploadWithProgress(
      file,
      { file_type: "logo", organization_id: organizationId },
      onProgress
    );
  };

  const handleAllFilesHandled = () => {
    // Invalidate the logos query to refetch the list
    queryClient.invalidateQueries({
      queryKey: ["organization", organizationId, "logos"],
    });
    // Mark ORG_UPLOAD_LOGO action as completed
    completeOrgUploadLogo();
    onUploadComplete?.();
  };

  if (!organizationId) return null;

  return (
    <LogosUpload
      uploadFileHandler={uploadFileHandler}
      onAllFilesHandled={handleAllFilesHandled}
      maxFiles={maxFiles}
      onUploadStateChange={onUploadStateChange}
      initialFiles={initialFiles}
      showRemoveButton={showRemoveButton}
      onFileRemove={onFileRemove}
    />
  );
}

export default LogosUploadContainer;

